import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader, Toast, Modal } from "@/components/Layout";
import api from "@/lib/api";

const errText = (e) => e?.response?.data?.detail || "Eroare";
const ACT = { cs: "Curs", lp: "Lucrări practice", sem: "Seminar" };
const SEM = { 1: "Sem. I", 2: "Sem. II" };

/** Atribuirea disciplinelor (activități alocate pe formațiuni) către catedre / clinici — pasul 1 al Notei de comandă. */
export default function OrderAssign() {
  const [params, setParams] = useSearchParams();
  const [years, setYears] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [units, setUnits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [d, setD] = useState(null);                    // detaliul planului ales
  const [bulkUnit, setBulkUnit] = useState("");
  const [splitting, setSplitting] = useState(null);    // { row, map: formationId -> unitId|"" }
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const toast = (text, type = "info") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 3500); };
  const yearId = params.get("year") || (years && (years.find(y => y.is_current) || years[0] || {}).id) || "";
  const programId = params.get("program") || "";
  const planId = params.get("plan") || "";
  const setQ = (patch) => { const p = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k))); setParams(p); };

  useEffect(() => {
    api.get("/stat-didactic/years").then(r => setYears(r.data)).catch(() => setYears([]));
    api.get("/stat-didactic/programs").then(r => setPrograms(r.data.filter(p => p.active))).catch(() => {});
    api.get("/stat-didactic/orders/units").then(r => setUnits(r.data.filter(u => u.selectable))).catch(() => {});
  }, []);
  useEffect(() => {
    if (programId && yearId) api.get("/stat-didactic/plans", { params: { program_id: programId, year_id: yearId } }).then(r => setPlans(r.data)); else setPlans([]);
  }, [programId, yearId]);
  const load = () => (planId ? api.get(`/stat-didactic/plans/${planId}/assignments`).then(r => setD(r.data)).catch(() => setD(null)) : setD(null));
  useEffect(() => { load(); }, [planId]);

  const unitName = (id) => units.find(u => u.id === id)?.path || "?";
  const saveRows = async (rows) => {
    try { const r = await api.put(`/stat-didactic/plans/${planId}/assignments`, { rows }); setD(r.data); return true; }
    catch (e) { toast(errText(e), "error"); return false; }
  };
  const toPayload = (r, parts) => ({ discipline_id: r.discipline_id, semester: r.semester, activity: r.activity, parts });
  const allRows = () => d.rows.map(r => toPayload(r, r.parts.map(p => ({ org_unit_id: p.org_unit_id, formation_ids: p.formation_ids }))));
  const setRowUnit = async (r, unitId) => {
    const rows = allRows().map(x => (x.discipline_id === r.discipline_id && x.semester === r.semester && x.activity === r.activity
      ? toPayload(r, unitId ? [{ org_unit_id: unitId, formation_ids: r.formation_ids }] : []) : x));
    if (await saveRows(rows)) toast("Atribuire salvată", "success");
  };
  const assignAll = async () => {
    try { const r = await api.post(`/stat-didactic/plans/${planId}/assignments/assign-all`, { org_unit_id: bulkUnit }); setD(r.data); toast("Activitățile neatribuite au fost atribuite", "success"); }
    catch (e) { toast(errText(e), "error"); }
  };
  const openSplit = (r) => {
    const map = {};
    r.formation_ids.forEach(f => { map[f] = ""; });
    r.parts.forEach(p => p.formation_ids.forEach(f => { map[f] = p.org_unit_id; }));
    setSplitting({ row: r, map });
  };
  const applySplit = async () => {
    const { row, map } = splitting;
    const byUnit = {};
    Object.entries(map).forEach(([f, u]) => { if (u) (byUnit[u] = byUnit[u] || []).push(f); });
    const parts = Object.entries(byUnit).map(([org_unit_id, formation_ids]) => ({ org_unit_id, formation_ids }));
    const rows = allRows().map(x => (x.discipline_id === row.discipline_id && x.semester === row.semester && x.activity === row.activity ? toPayload(row, parts) : x));
    if (await saveRows(rows)) { setSplitting(null); toast("Împărțire salvată", "success"); }
  };

  if (years === null) return <div>Se încarcă...</div>;
  const formName = (id) => d?.formations.find(f => f.id === id)?.name || id;
  return (
    <>
      <PageHeader title="Atribuire către catedre" subtitle="Fiecare activitate alocată pe formațiuni se atribuie unei catedre / clinici (sau se împarte între mai multe)"
        actions={<Link className="btn btn-secondary" to={`/stat-didactic/nota?year=${yearId}`}>Nota de comandă →</Link>} />
      <Toast msg={msg.text} type={msg.type} />
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div><label className="label">An universitar</label>
          <select className="select" value={yearId} onChange={e => setQ({ year: e.target.value, plan: "" })} data-testid="sfd-oa-year">{years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}</select></div>
        <div style={{ minWidth: 260 }}><label className="label">Program</label>
          <select className="select" value={programId} onChange={e => setQ({ program: e.target.value, plan: "" })} data-testid="sfd-oa-program"><option value="">— alege —</option>{programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        <div><label className="label">An de studiu</label>
          <select className="select" value={planId} onChange={e => setQ({ plan: e.target.value })} data-testid="sfd-oa-plan"><option value="">— alege —</option>{plans.map(p => <option key={p.id} value={p.id}>Anul {p.study_year}</option>)}</select></div>
      </div>
      {!d ? <div className="card" style={{ padding: 24, color: "#64748B" }}>Alege programul și anul de studiu. Planul trebuie să aibă alocarea pe semestre făcută (vezi <Link to="/stat-didactic/planuri">Planuri de învățământ</Link>).</div> : (
        <>
          {!d.cohort && <div className="te-wait">Nu există efectiv și formațiuni pentru acest an de studiu; atribuirea are nevoie de ele (<Link to="/stat-didactic/formatiuni">Formațiuni de studii</Link>).</div>}
          {d.approved && <div className="te-closed" data-testid="sfd-oa-approved">Notele de comandă ale anului {d.year.name} sunt avizate; atribuirile nu se mai pot modifica până la redeschiderea avizării.</div>}
          <div className="card" style={{ padding: 14, marginBottom: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ minWidth: 320 }}><label className="label">Atribuie tot ce nu e atribuit încă la</label>
              <select className="select" value={bulkUnit} onChange={e => setBulkUnit(e.target.value)} data-testid="sfd-oa-bulk-unit"><option value="">— alege catedra —</option>{units.map(u => <option key={u.id} value={u.id}>{u.path}</option>)}</select></div>
            <button className="btn btn-primary" disabled={!bulkUnit || d.approved || d.unassigned_total === 0} onClick={assignAll} data-testid="sfd-oa-bulk">Atribuie</button>
            <span style={{ flex: 1 }} />
            <span data-testid="sfd-oa-left" style={{ fontSize: 13, color: d.unassigned_total ? "#92400E" : "#166534", fontWeight: 600 }}>{d.unassigned_total ? `${d.unassigned_total} formațiuni neatribuite` : "Totul este atribuit"}</span>
          </div>
          <div className="card" style={{ overflowX: "auto" }}>
            <table className="tbl" style={{ fontSize: 12.5, minWidth: 920 }}>
              <thead><tr><th>Disciplina</th><th>Sem.</th><th>Activitate</th><th>Ore/săpt.</th><th>Formațiuni</th><th>Catedra / clinica</th><th></th></tr></thead>
              <tbody>{d.rows.map((r, i) => {
                const single = r.parts.length === 1 && r.unassigned_ids.length === 0;
                return (
                  <tr key={i} data-testid={`sfd-oa-row-${r.discipline}-${r.semester}-${r.activity}`}>
                    <td style={{ fontWeight: 600 }}>{r.discipline}</td><td>{SEM[r.semester]}</td><td>{ACT[r.activity]}</td><td>{r.hours_week}</td><td>{r.n_formations}</td>
                    <td>
                      <select className="select" style={{ padding: "3px 4px", maxWidth: 360 }} disabled={d.approved} value={single ? r.parts[0].org_unit_id : (r.parts.length ? "__split" : "")}
                        onChange={e => e.target.value !== "__split" && setRowUnit(r, e.target.value)}>
                        <option value="">— neatribuită —</option>
                        {r.parts.length > 1 || (r.parts.length === 1 && !single) ? <option value="__split">Împărțită ({r.parts.length} catedre{r.unassigned_ids.length ? `, ${r.unassigned_ids.length} neatribuite` : ""})</option> : null}
                        {units.map(u => <option key={u.id} value={u.id}>{u.path}</option>)}
                      </select>
                    </td>
                    <td><button className="btn btn-ghost" style={{ padding: "1px 8px", fontSize: 12 }} disabled={d.approved || r.n_formations === 0} onClick={() => openSplit(r)}>Împarte…</button></td>
                  </tr>);
              })}</tbody>
            </table>
            {d.rows.length === 0 && <div style={{ padding: 16, color: "#94A3B8", fontSize: 13 }}>Planul nu are activități cu ore. Completează planul și alocarea.</div>}
          </div>
        </>)}
      {splitting && (
        <Modal title={`Împarte: ${splitting.row.discipline} — ${ACT[splitting.row.activity]}, ${SEM[splitting.row.semester]}`} onClose={() => setSplitting(null)} width={680}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ fontSize: 12.5 }}>Setează toate la:</span>
            <select className="select" style={{ maxWidth: 320 }} onChange={e => setSplitting({ ...splitting, map: Object.fromEntries(Object.keys(splitting.map).map(k => [k, e.target.value])) })}>
              <option value="">— neatribuite —</option>{units.map(u => <option key={u.id} value={u.id}>{u.path}</option>)}</select>
          </div>
          <div style={{ maxHeight: 360, overflow: "auto", display: "grid", gap: 6 }}>
            {Object.keys(splitting.map).map(fid => (
              <div key={fid} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5 }}>
                <span style={{ width: 220 }}>{formName(fid)}</span>
                <select className="select" style={{ padding: "3px 4px", flex: 1 }} value={splitting.map[fid]} onChange={e => setSplitting({ ...splitting, map: { ...splitting.map, [fid]: e.target.value } })} data-testid={`sfd-oa-split-${formName(fid)}`}>
                  <option value="">— neatribuită —</option>{units.map(u => <option key={u.id} value={u.id}>{u.path}</option>)}</select>
              </div>))}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={() => setSplitting(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={applySplit} data-testid="sfd-oa-split-save">Salvează împărțirea</button>
          </div>
        </Modal>)}
    </>
  );
}
